"use client";

import React, { useEffect, useRef, useState } from "react";
import { Progress } from "@/components/ui/progress";
import { Activity, Clock, Users, ShieldCheck } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
function AnimatedNumber({
  value,
  isVisible
}: {
  value: number;
  isVisible: boolean;
}) {
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let startTimestamp: number | null = null;
    const duration = 2000;
    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const easeProgress = 1 - Math.pow(1 - progress, 4);
      setCurrent(Math.floor(easeProgress * value));
      if (progress < 1) {
        window.requestAnimationFrame(step);
      } else {
        setCurrent(value);
      }
    };
    window.requestAnimationFrame(step);
  }, [value, isVisible]);
  return <>{current}</>;
}
const statsData = [{
  icon: ShieldCheck,
  value: 99,
  suffix: ".9%",
  title: "System Uptime",
  description: "High-availability infrastructure designed for mission-critical enterprise applications.",
  progressValue: 99
}, {
  icon: Activity,
  value: 150,
  suffix: "+",
  title: "Successful Deploys",
  description: "Production-ready solutions delivered across cloud, mobile, and web ecosystems.",
  progressValue: 75
}, {
  icon: Users,
  value: 10,
  suffix: "M+",
  title: "Active Users",
  description: "Scalable architectures supporting millions of concurrent global interactions daily.",
  progressValue: 90
}, {
  icon: Clock,
  value: 2,
  suffix: "x",
  title: "Growth Velocity",
  description: "Accelerating product time-to-market through agile methodology and clean code.",
  progressValue: 100
}];
export default function ClinicalImpactByTheNumbers() {
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="223267" id="proven-impact" className="bg-background text-foreground py-24 relative overflow-hidden" ref={sectionRef}>
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/5 rounded-lg blur-[120px] pointer-events-none opacity-40" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-secondary/5 rounded-lg blur-[100px] pointer-events-none opacity-20" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-7xl">
        
        {}
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-default font-bold mb-6 tracking-tighter">
            Proven <span className="text-primary">Impact</span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-sans">DEVLUX builds high-performance marketing systems that drive measurable growth through paid media, organic content, and creator marketing.</p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {statsData.map((stat, i) => <div key={i} data-index={i} className={`relative group rounded-md border border-border bg-card/30 backdrop-blur-sm p-8 shadow-md transition-all duration-700 ease-out hover:bg-card/50 hover:border-primary/50 hover:shadow-lg hover:-translate-y-1 ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
          transitionDelay: `${i * 150}ms`
        }}>
              {}
              <div className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-md bg-primary/5 text-primary ring-1 ring-primary/30">
                <stat.icon className="h-6 w-6" />
              </div>

              {}
              <div className="mb-2 flex items-baseline gap-1">
                <span className="text-5xl md:text-6xl font-mono font-bold text-primary tracking-tighter">
                  <AnimatedNumber value={stat.value} isVisible={sectionVisible} />
                </span>
                <span className="text-3xl md:text-4xl font-mono font-bold text-primary/90">
                  {stat.suffix}
                </span>
              </div>

              {}
              <h3 className="text-xl font-default font-bold text-card-foreground mb-3">
                {stat.title}
              </h3>
              <p className="text-sm text-muted-foreground font-sans mb-8 leading-relaxed">
                {stat.description}
              </p>

              {}
              <div className="absolute bottom-8 left-8 right-8">
                <Progress value={sectionVisible ? stat.progressValue : 0} className="h-1.5 bg-muted/30" />
              </div>
            </div>)}
        </div>

      </div>
    </section>;
}